import { useState } from "react";
import { Video } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { request } from "@/lib/api";
import { consultationSlots, getEarliestBookingDateIso, getLatestBookingDateIso } from "@/lib/appointments";

const SPECIALTIES = [
  "General Practitioner",
  "Cardiologist",
  "Oncologist",
  "Dermatologist",
  "Orthopedic",
  "Gynaecologist",
  "ENT",
  "Endocrinology",
  "Gastroenterologist",
  "Paediatrician",
];

export default function ConsultationsPage() {
  const { token } = useAuth();
  const [specialty, setSpecialty] = useState(SPECIALTIES[0]);
  const [appointmentDate, setAppointmentDate] = useState(getEarliestBookingDateIso());
  const [appointmentSlot, setAppointmentSlot] = useState(consultationSlots[0]);
  const [concern, setConcern] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isSending, setIsSending] = useState(false);

  async function submitConsultation(event) {
    event.preventDefault();
    setIsSending(true);
    setError("");
    try {
      const result = await request(
        "/portal/consultations",
        { method: "POST", body: JSON.stringify({ specialty, appointment_date: appointmentDate, appointment_slot: appointmentSlot, concern }) },
        token
      );
      setMessage(`${result.consultation_id} · ${result.message}`);
      setConcern("");
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setIsSending(false);
    }
  }

  return <section className="page-stack" data-testid="consultations-page"><div className="page-heading"><p className="eyebrow">Online consultations</p><h1>Talk to a doctor, whenever you need.</h1><p>Unlimited online consultations with MBBS doctors and specialists.</p></div><section className="consult-hero" data-testid="consultation-hero"><img src="https://images.pexels.com/photos/7195308/pexels-photo-7195308.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="Doctor on a video consultation" /><div><Video size={24} /><h2>Care from wherever you are</h2><p>Tell us what you need and select a convenient time.</p></div></section><form className="consultation-form" onSubmit={submitConsultation} data-testid="consultation-request-form"><label htmlFor="specialty">Who would you like to consult?</label><select id="specialty" value={specialty} onChange={(event) => setSpecialty(event.target.value)} data-testid="consultation-specialty-select">{SPECIALTIES.map((option) => <option key={option}>{option}</option>)}</select><label htmlFor="consultation-date">Appointment date</label><input id="consultation-date" type="date" min={getEarliestBookingDateIso()} max={getLatestBookingDateIso()} value={appointmentDate} onChange={(event) => setAppointmentDate(event.target.value)} data-testid="consultation-date-input" /><label htmlFor="consultation-slot">Preferred time</label><select id="consultation-slot" value={appointmentSlot} onChange={(event) => setAppointmentSlot(event.target.value)} data-testid="consultation-time-select">{consultationSlots.map((slot) => <option key={slot}>{slot}</option>)}</select><label htmlFor="health-concern">What can the doctor help with?</label><textarea id="health-concern" value={concern} onChange={(event) => setConcern(event.target.value)} placeholder="Share a brief description" required data-testid="consultation-concern-input" />{error && <p className="form-error" role="alert" data-testid="consultation-error-message">{error}</p>}{message && <p className="success-inline" data-testid="consultation-success-message">{message}</p>}<Button type="submit" disabled={isSending} data-testid="submit-consultation-button">{isSending ? "Sending request…" : "Request consultation"}</Button></form></section>;
}
